import { EditOutlined } from '@ant-design/icons';
import { Button, TablePaginationConfig } from 'antd';
import Column from 'antd/es/table/Column';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { paths } from '@app/constants';
import { createExpectedAnswer } from '@app/utils';
import { usePermissions } from '@data/hooks';
import {
  CriteriaStatus,
  QuestionCriterionView,
  QuestionType,
  MatchingTrialStatus,
} from '@data/sdk/match';
import { Namespace } from '@data/utils/enums';
import { AntdGrid, AntdTable, AntdTooltip, AntdTypography } from '@ui/components';

const ICD_CODES_MAX_LENGTH = 10;

const { useBreakpoint } = AntdGrid;
const { Text } = AntdTypography;

const getEditCriteriaDisabled = (criteria: QuestionCriterionView, questionType: QuestionType) => {
  return (
    [
      MatchingTrialStatus.Proposed,
      MatchingTrialStatus.Deleted,
      MatchingTrialStatus.Completed,
    ].includes(criteria.matchingTrialStatus as typeof MatchingTrialStatus.Proposed) ||
    criteria.status === CriteriaStatus.Deleted ||
    questionType === QuestionType.Distractor
  );
};

type TrialsTableProps = {
  criterias: QuestionCriterionView[] | undefined;
  current: number;
  handleOpenEditCriteria: (criteria: QuestionCriterionView) => void;
  handleTableChange: ({ pageSize, current }: TablePaginationConfig) => void;
  isLoading: boolean;
  pageSize: number;
  questionType: QuestionType;
  total: number;
};

export const TrialsTable = ({
  criterias,
  isLoading,
  current,
  pageSize,
  total,
  questionType,
  handleTableChange,
  handleOpenEditCriteria,
}: TrialsTableProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { IS_REVIEWER } = usePermissions();
  const { xl } = useBreakpoint();

  return (
    <AntdTable
      className="mt-4"
      dataSource={criterias}
      loading={isLoading}
      pagination={{
        current,
        pageSize,
        total,
        placement: ['bottomStart'],
      }}
      onChange={handleTableChange}
      scroll={{ x: xl ? undefined : 'max-content' }}
    >
      <Column title={t('question_page.trials.table.trial_id')} dataIndex="trialId" width={150} />
      <Column
        title={t('question_page.trials.table.trial_name')}
        dataIndex="trialTitle"
        width="35%"
        render={(name: string, criterion: QuestionCriterionView) => (
          <AntdTooltip title={name}>
            <Link
              to={`/${paths.app}/${paths.trial_recruitment}/${paths.trials}/${criterion.id}`}
              className="text-primary-600 break-all line-clamp-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              {name}
            </Link>
          </AntdTooltip>
        )}
      />
      <Column
        title={t('question_page.trials.table.matching_criteria_text')}
        dataIndex="criterionText"
        width="35%"
      />
      <Column
        title={t('question_page.trials.table.lead_sponsor')}
        dataIndex="leadSponsor"
        width="20%"
      />
      <Column
        title={t('question_page.trials.table.expected_answer')}
        dataIndex="expectedAnswer"
        width={200}
        render={(_, criterion: QuestionCriterionView) => (
          <Text>{createExpectedAnswer(criterion)}</Text>
        )}
      />
      <Column
        title={t('question_page.trials.table.icd_codes')}
        dataIndex="icdCodes"
        width={200}
        render={(icdCodes: string[], criterion: QuestionCriterionView) => {
          if (icdCodes.length > ICD_CODES_MAX_LENGTH) {
            return (
              <AntdTooltip
                title={
                  <Trans
                    t={t}
                    i18nKey="question_page.trials.table.icd_codes_tooltip"
                    values={{ count: icdCodes.length - ICD_CODES_MAX_LENGTH }}
                    components={[
                      <Link
                        key="link"
                        to={`/${paths.app}/${paths.trial_recruitment}/${paths.trials}/${criterion.id}`}
                        target="_blank"
                        className="text-white underline"
                        rel="noopener noreferrer"
                      />,
                    ]}
                  />
                }
              >{`${icdCodes.slice(0, ICD_CODES_MAX_LENGTH).join(', ')}...`}</AntdTooltip>
            );
          }
          return icdCodes.join(', ');
        }}
      />
      {!IS_REVIEWER && (
        <Column
          dataIndex="action"
          width={100}
          render={(_, criterion: QuestionCriterionView) => {
            const disabled = getEditCriteriaDisabled(criterion, questionType);
            return (
              <AntdTooltip
                title={
                  disabled
                    ? t([`edit_trial.${criterion.matchingTrialStatus}`, 'edit_trial.default'])
                    : ''
                }
                placement="topLeft"
              >
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenEditCriteria(criterion)}
                  disabled={getEditCriteriaDisabled(criterion, questionType)}
                />
              </AntdTooltip>
            );
          }}
        />
      )}
    </AntdTable>
  );
};
